import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { PhotoThumb } from "@/components/PhotoThumb";

export default function Attempts() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/attempts").then((r) => setList(r.data)); }, []);
  return (
    <>
      <PageHeader eyebrow="Service Attempts" title="Field Log" description="Every attempted serve — GPS, timestamp, outcome." />
      <div className="p-4 sm:p-8">
        <div className="md:hidden space-y-2" data-testid="attempts-cards">
          {list.length === 0 ? <div className="text-center py-14 text-slate-400 text-sm">No attempts yet.</div> : list.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap"><Link to={`/jobs/${a.job_id}`} className="sm-link text-xs">{a.job_number || "—"}</Link><OutcomeBadge outcome={a.outcome} /><span className="ml-auto text-xs text-slate-500">{new Date(a.attempted_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span></div>
              <div className="font-semibold text-slate-900 mt-1">{a.recipient_name || "—"}</div>
              <div className="text-xs text-slate-600">{a.address}</div>
              <div className="text-xs text-slate-500 mt-1">{a.server_name || "—"}{a.gps_lat ? ` · ${a.gps_lat.toFixed(4)}, ${a.gps_lng.toFixed(4)}` : ""}</div>
              {a.photos?.length > 0 && <div className="flex gap-1 mt-2">{a.photos.map((p) => <PhotoThumb key={p.id} photo={p} size="h-12 w-12" />)}</div>}
            </div>
          ))}
        </div>
        <Card className="border-slate-200 overflow-hidden hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attempts-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-mono uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">When</th>
                  <th className="text-left px-5 py-3 font-semibold">Job</th>
                  <th className="text-left px-5 py-3 font-semibold">Recipient</th>
                  <th className="text-left px-5 py-3 font-semibold">Address</th>
                  <th className="text-left px-5 py-3 font-semibold">GPS</th>
                  <th className="text-left px-5 py-3 font-semibold">Outcome</th>
                  <th className="text-left px-5 py-3 font-semibold">Server</th>
                  <th className="text-left px-5 py-3 font-semibold">Photos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-14 text-slate-400 font-mono">No attempts yet.</td></tr>
                ) : list.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono text-xs text-slate-700">{new Date(a.attempted_at).toLocaleString()}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-700"><Link to={`/jobs/${a.job_id}`} className="sm-link">{a.job_number || "—"}</Link></td>
                    <td className="px-5 py-3 text-slate-900 font-semibold">{a.recipient_name || "—"}</td>
                    <td className="px-5 py-3 text-slate-700 max-w-[280px] truncate">{a.address}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">
                      {a.gps_lat ? (
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.gps_lat.toFixed(4)}, {a.gps_lng.toFixed(4)}</span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3"><OutcomeBadge outcome={a.outcome} /></td>
                    <td className="px-5 py-3 text-slate-700">{a.server_name || "—"}</td>
                    <td className="px-5 py-3"><div className="flex gap-1">{(a.photos || []).map((p) => <PhotoThumb key={p.id} photo={p} size="h-9 w-9" />)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}

function OutcomeBadge({ outcome }) {
  const map = {
    served: "bg-emerald-100 text-emerald-800 border-emerald-200",
    not_home: "bg-amber-100 text-amber-800 border-amber-200",
    bad_address: "bg-rose-100 text-rose-800 border-rose-200",
    refused: "bg-rose-100 text-rose-800 border-rose-200",
    moved: "bg-slate-100 text-slate-700 border-slate-300",
    other: "bg-slate-100 text-slate-700 border-slate-300",
  };
  return <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[outcome] || map.other}`}>{outcome?.replace("_", " ")}</span>;
}
